import * as yup from 'yup';
import {
  TemplateList,
  TemplateNames,
  TemplateCustom,
  UpdatableNotificationOptions,
  UpdatableNotificationTemplateList,
  UpdatableNotificationTemplateMarkdown,
  UpdatableNotificationTemplateCustom,
  CustomTemplateData,
} from '../../../client/templates';
import { UpdatePayload } from '../../../client/internal';
import { formSchema } from '../forms/fieldSchemas';
import { ButtonsSchema } from './schemas';
import { FlatLayout } from '../../utils/templates/flattenLayout';

const BaseNotificationUpdateSchema = yup.object({
  id: yup.string().required(),
  buttons: ButtonsSchema.clone(),
  customData: yup.object().optional().default({}),
});

const MarkdownTemplateUpdateSchema: yup.SchemaOf<UpdatableNotificationTemplateMarkdown> = BaseNotificationUpdateSchema.clone()
  .shape({
    template: yup.mixed<'markdown'>().required(),
    body: yup.string().optional().default(null),
    form: formSchema.nullable().optional().default(null),
  })
  .label('Notification Update (Markdown)');

const ListTemplateUpdateSchema: yup.SchemaOf<UpdatableNotificationTemplateList> = BaseNotificationUpdateSchema.clone()
  .shape({
    template: yup.mixed<'list'>().required(),
    list: yup
      .mixed<TemplateList['list']>()
      .optional()
      .test({
        name: 'List object',
        message: 'Invalid list object',
        test: (value?: Record<string, string>) => {
          // undefined - no update.
          if (!value) {
            return true;
          }
          if (value && typeof value !== 'object') {
            throw new Error('Not an object');
          }
          return Object.entries(value ?? {}).every(
            ([key, val]) => typeof key === 'string' && typeof val === 'string'
          );
        },
      }),
  })
  .label('Update Notification (List)');

const CustomTemplateUpdateSchema: yup.SchemaOf<UpdatableNotificationTemplateCustom> = BaseNotificationUpdateSchema.clone()
  .shape({
    template: yup.mixed<'custom'>().required(),
    templateData: yup
      .mixed<TemplateCustom['templateData']>()
      .optional()
      .test({
        name: 'Temp object',
        message: 'Invalid list object',
        test: function (value?: CustomTemplateData) {
          // undefined - no update.
          if (!value) {
            return true;
          }
          const fragments: FlatLayout = this.options?.context?.fragments;
          fragments.presentationFragments.forEach((fragment) => {
            const fragmentData = value[fragment.dataKey];
            if (!fragment.optional && !fragmentData) {
              throw new Error(
                `templateData["${fragment.dataKey}"] is not optional and must exist in template data.`
              );
            }
            if (fragmentData) {
              if (
                (fragment.type === 'text' || fragment.type === 'image') &&
                typeof fragmentData !== 'string'
              ) {
                throw new Error(
                  `templateData["${fragment.dataKey}"] is referred by a ${fragment.type} fragment and must be a string type.`
                );
              }
              if (
                fragment.type === 'list' &&
                (!Array.isArray(fragmentData) ||
                  fragmentData.some((pair) => {
                    return (
                      pair.length !== 2 ||
                      typeof pair[0] !== 'string' ||
                      typeof pair[1] !== 'string'
                    );
                  }))
              ) {
                throw new Error(
                  `templateData["${fragment.dataKey}"] is referred by a list fragment and must be a ListPairs (Array of [string, string]) type.`
                );
              }
            }
          });
          return true;
        },
      }),
  })
  .label('Update Notification (Custom)');

const templateSchemaMap = {
  [TemplateNames.markdown]: MarkdownTemplateUpdateSchema,
  [TemplateNames.list]: ListTemplateUpdateSchema,
  [TemplateNames.custom]: CustomTemplateUpdateSchema,
};

type ValidSchemaTypes =
  | yup.SchemaOf<UpdatableNotificationTemplateList>
  | yup.SchemaOf<UpdatableNotificationTemplateMarkdown>
  | yup.SchemaOf<UpdatableNotificationTemplateCustom>;

export const getTemplateSchema = (
  payload: UpdatePayload<UpdatableNotificationOptions>
): ValidSchemaTypes => {
  if (!payload.template) {
    throw new Error(`Template type cannot be null`);
  }
  const template = templateSchemaMap[payload?.template];
  if (!template) {
    throw new Error(`Cannot find template type: ${payload.template}`);
  }
  return template;
};
